import React, { useState, FunctionComponent, Dispatch, SetStateAction } from 'react';
import { Bar, Line } from '@reactchartjs/react-chart.js';
import { useQuery } from 'react-query';
import { Typography, Button, Radio, RadioGroup, FormControlLabel, IconButton } from '@material-ui/core';
import { ApiService } from '../../services/api';
import styled from 'styled-components';
import { ApiError } from '../../types/api_error';
import { GraphResponse } from '../../types/dashboard_data';
import { DateRange } from '@material-ui/icons';
import { useClientId } from '../Routes/useClientId';
import { LoadingIndicator, DatePickerModal } from '../../components';
import { getGraphContent, getGraphOptions } from './GraphUtils';
import { GraphLabels } from '../../types/graph';

const GraphWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 10px;
`;

const InfoWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const StyledRadioGroup = styled(RadioGroup)`
  display: flex;
  flex-direction: column;
`;

const StyledIconButton = styled(IconButton)`
  display: flex;
  padding: 4px !important;
  border-radius: 0;
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  & .MuiTypography-body1 {
    font-size: 12px;
  }
`;

type TypeGraphProps = {
  mplaceId?: string;
  types: GraphLabels;
  setTypes: React.Dispatch<React.SetStateAction<GraphLabels>>;
  isLineChart: string;
  setIsLineChart: (name: string) => void;
  startDate?: string;
  setStartDate: Dispatch<SetStateAction<string | undefined>>;
  endDate?: string;
  setEndDate: Dispatch<SetStateAction<string | undefined>>;
};

export const TypeGraphs: FunctionComponent<TypeGraphProps> = ({
  mplaceId,
  types,
  setTypes,
  isLineChart,
  setIsLineChart,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
}) => {
  const clientId = useClientId();
  const [datePickerOpen, setDatePickerOpen] = useState<boolean>(false);
  const [graphReady, setGraphReady] = useState(false);

  const { data: graphData, isLoading: graphIsLoading, isError: graphIsError, refetch: graphRefetch } = useQuery<
    GraphResponse,
    ApiError
  >(
    [mplaceId ? `invoice-graph-data-${clientId}-${mplaceId}` : `invoice-graph-data-${clientId}`, clientId],
    ApiService.get(`/client/${clientId}/invoices/consumption`, { startDate, endDate, mplaceId }),
    {
      onSuccess: (data) => {
        const mappedTypes: GraphLabels = {};
        data.labels.forEach((label) => (mappedTypes[label.toUpperCase()] = true));
        if (Object.keys(types).length === 1) setTypes({ ...types, ...mappedTypes });
        setGraphReady(true);
      },
    }
  );

  const handleModalSubmit = () => {
    setGraphReady(false);
    graphRefetch();
    setDatePickerOpen(false);
  };

  const barOpts = getGraphOptions(
    {
      yAxes: [
        {
          stacked: false,
          ticks: {
            beginAtZero: true,
          },
        },
      ],
      xAxes: [
        {
          stacked: false,
        },
      ],
    },
    setTypes
  );

  const lineOpts = getGraphOptions(
    {
      yAxes: [
        {
          type: 'linear',
          display: true,
          position: 'left',
          id: 'y-axis-1',
        },
      ],
    },
    setTypes
  );
  const refetchAll = () => {
    graphRefetch();
  };

  if (graphIsLoading || !graphReady) {
    return <LoadingIndicator />;
  }

  if (!clientId || graphIsError || !graphData) {
    return (
      <InfoWrapper>
        <Typography variant="body2">Nažalost nismo uspjeli dohvatiti podatke.</Typography>
        <Button color="primary" onClick={refetchAll}>
          Pokušaj ponovno
        </Button>
      </InfoWrapper>
    );
  }

  const barGraphContent = getGraphContent(graphData, types, true);
  const lineGraphContent = getGraphContent(graphData, types, false);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsLineChart(event.target.value);
  };

  return (
    <GraphWrapper>
      <HeaderRow>
        <StyledRadioGroup aria-label="gender" name="gender1" value={isLineChart} onChange={handleChange}>
          <StyledFormControlLabel value="bar" control={<Radio />} label="Stupčasti prikaz" />
          <StyledFormControlLabel value="line" control={<Radio />} label="Linijski prikaz" />
        </StyledRadioGroup>
        <StyledIconButton onClick={() => setDatePickerOpen(true)}>
          <DateRange color="primary" />
          <Typography variant="caption">Odaberite razdoblje</Typography>
        </StyledIconButton>
      </HeaderRow>
      {isLineChart === 'line' ? (
        <Line type="line" data={lineGraphContent} options={lineOpts} />
      ) : (
        <Bar type="bar" data={barGraphContent} options={barOpts} />
      )}
      <DatePickerModal
        open={datePickerOpen}
        handleClose={() => setDatePickerOpen(false)}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        startDate={startDate}
        endDate={endDate}
        handleSubmit={handleModalSubmit}
        title="Odaberite period koji će biti prikazan na grafu"
      />
    </GraphWrapper>
  );
};
